import type { CaseStudy } from './types';

/**
 * Case studies for the Financial section.
 *
 * The first six are ported from Realiform unchanged — same prose, same
 * economics, same risk sections. They are US micro-business teardowns with no
 * Korea angle, and that is deliberate: `/financial` carries an editorial intro
 * explaining why wealth-building mechanics sit on a diaspora site, rather than
 * retrofitting a Korean framing onto analysis that did not have one.
 *
 * The last three were written for this audience. They teach mechanisms that
 * only bite people operating across the US–Korea line: succession inside the
 * community, cross-border tax residency, and heritage-language demand. They
 * carry a `diasporaAngle` and cite primary sources, because unlike the ported
 * six they make claims about rules a reader can go and check.
 *
 * PROVENANCE IS NOT DECORATION. Every record is `illustrative` or `composite`
 * — the economics are modelled from publicly reported ranges for each business
 * type, not lifted from a specific named operator. When a real interview
 * lands, swap `sourceType` to `operator-interview` and set `verifiedAt`; the UI
 * badges the difference for the reader either way. See `/financial/methodology`.
 */

export const CASE_STUDIES: CaseStudy[] = [
  {
    id: "cs_laundromat_seller_financed",
    slug: "laundromat-seller-financed",
    title: "A 24-machine laundromat bought on a seller note",
    dek: "$69k of cash into a $285k business, with the retiring owner carrying $242k of paper for ten years.",
    category: "local-cashflow",
    market: "Midwest US, secondary metro",

    startupCostCents: 28_500_000,
    cashInvestedCents: 6_900_000,
    monthlyRevenueCents: 1_583_300,
    // Operating costs ($11,200) plus debt service on the $242,250 seller note
    // at 7% over ten years ($2,813). Quoting a laundromat's "monthly net"
    // before the note is exactly the inflation this site exists to call out.
    monthlyExpenseCents: 1_401_300,
    hoursPerWeek: 6,
    monthsToBreakEven: 38,

    effort: "part-time",
    difficulty: "intermediate",

    body: `Laundromats sit in the narrow band of businesses that are genuinely semi-absentee: the machines run whether or not you are in the building, and the customer serves themselves. That is also why they are consistently overpriced — everyone reads the same pitch.

The deal that works is rarely the listed one. This teardown follows a shop where the seller was 71, had no succession plan, and cared more about a clean exit than a maximum price. He carried $242k of the $285k over ten years at 7%, which is the entire reason the numbers clear.

The trap is the equipment. Commercial washers last 10–15 years, and a full re-equip on a 24-machine store runs past $90k. A store with machines at year twelve is not cheaper than one with new machines — it is a deferred bill with interest.`,

    playbookSteps: [
      {
        title: "Find the operators who are not listing",
        detail:
          "Walk every laundromat within 40 minutes on a weekday morning. Note which ones have handwritten signage, no card readers, and an owner on site. Those are the ones where a direct approach beats a broker listing.",
        durationLabel: "Weeks 1–6",
      },
      {
        title: "Read the water bill, not the P&L",
        detail:
          "Water and sewer usage is the one number a seller cannot inflate. Pull 24 months of utility bills and back into turns per machine per day. If claimed revenue implies more turns than the water supports, the revenue is fiction.",
        durationLabel: "Weeks 6–8",
      },
      {
        title: "Age every machine",
        detail:
          "Serial numbers date each unit. Build a replacement schedule and price it. This becomes both your capex reserve and your strongest lever on price.",
        durationLabel: "Week 8",
      },
      {
        title: "Structure around the seller's actual motivation",
        detail:
          "A seller who wants out cleanly will often take a note at a below-market rate for a higher headline price. That trade is usually worth taking — the financing terms move your cash-on-cash far more than the price does.",
        durationLabel: "Weeks 9–12",
      },
      {
        title: "Fix the two things that lift revenue immediately",
        detail:
          "Card payment and extended hours. Coin-only stores leave real money on the table, and the conversion pays back inside a year at this size.",
        durationLabel: "Months 1–3",
      },
    ],

    techStack: [
      "Card system (CCI / ESD)",
      "Remote machine monitoring",
      "QuickBooks Online",
      "Google Business Profile",
      "Security cameras with remote access",
    ],

    risks: [
      "Equipment age is the whole deal. A store bought at year twelve of a fifteen-year equipment life is a capex bill wearing a business costume.",
      "Lease term must outlast the loan. A five-year lease against a ten-year note means renegotiating from zero leverage in year five.",
      "Water and sewer rates are set by the municipality and have risen faster than general inflation in many metros.",
      "Semi-absentee still means showing up. Vandalism, breakdowns, and flooding do not wait for a convenient week.",
    ],

    tags: ["seller-financing", "semi-absentee", "equipment-heavy", "acquisition"],

    sourceType: "composite",
    sourceNote:
      "Economics modelled from publicly reported laundromat listing data and industry equipment-life figures. Not a specific named business.",
    verifiedAt: '2026-06-14',

    featured: true,
    publishedAt: '2026-06-20',
    readingMinutes: 11,
    gradient: "from-[#0047AB] via-[#003A8A] to-[#1C1C1C]",
  },

  {
    id: "cs_micro_saas_invoice",
    slug: "micro-saas-invoice-tool",
    title: "A $19/mo invoicing tool for a single trade",
    dek: "Built in eleven weekends, sold only to electricians, at $6.4k MRR with one founder.",
    category: "micro-saas",
    market: "US + Canada, remote",

    startupCostCents: 480_000,
    cashInvestedCents: 480_000,
    monthlyRevenueCents: 640_000,
    monthlyExpenseCents: 118_000,
    hoursPerWeek: 12,
    monthsToBreakEven: 7,

    effort: "part-time",
    difficulty: "intermediate",

    body: `The generic version of this product has forty competitors and costs $200 a month in ads to acquire a customer. The version that works picks one trade and speaks its language.

The wedge here is the compliance detail: electrical work in most jurisdictions requires the permit number and inspection date on the final invoice. Generic invoicing tools make that a custom field. Doing it natively — and pre-filling it from the permit portal — is a small feature that removes a real weekly annoyance, and it is the entire reason people switch.

The distribution channel is not SEO. It is the trade associations, the supply-house counter, and three regional Facebook groups with 40,000 members between them.`,

    playbookSteps: [
      {
        title: "Pick the trade by its paperwork",
        detail:
          "The best niches have a mandatory document that generic software handles badly. Electrical permits, HVAC refrigerant logs, food-service temperature records. Paperwork pain converts better than feature preference.",
        durationLabel: "Weeks 1–2",
      },
      {
        title: "Sell twenty before building",
        detail:
          "Post in the trade groups offering lifetime access at $199 to the first twenty who describe their current invoicing process. You are buying requirements, and the money proves the pain is real.",
        durationLabel: "Weeks 2–5",
      },
      {
        title: "Build only the wedge",
        detail:
          "Invoice, permit field, PDF, payment link. Nothing else. The temptation to add scheduling, estimates, and inventory is what turns an eleven-weekend project into an eighteen-month one.",
        durationLabel: "Weeks 5–16",
      },
      {
        title: "Charge monthly from day one",
        detail:
          "The lifetime deals were for validation. Everyone after pays $19/month. Introducing a subscription later to an audience trained on one-time pricing is far harder than starting there.",
        durationLabel: "Week 16",
      },
      {
        title: "Work the supply house",
        detail:
          "A counter card at three regional electrical suppliers outperformed every paid channel tried. The customers are standing there every week already.",
        durationLabel: "Ongoing",
      },
    ],

    techStack: [
      "Next.js on Cloudflare Workers",
      "D1 (SQLite)",
      "Stripe Billing",
      "Resend for transactional email",
      "Clerk for auth",
      "Plausible",
    ],

    risks: [
      "Single-founder concentration. Support, sales, and deploys all route to one person, and that is the binding constraint on growth.",
      "Niche ceiling is real. There are only so many electricians who will pay $19/month, and this reaches a natural plateau well under $30k MRR.",
      "Platform dependency — if a generic incumbent ships native permit fields, the wedge closes.",
      "Churn tracks the trade's own business cycle. Contractors who slow down cancel software first.",
    ],

    tags: ["niche-saas", "bootstrapped", "trade-services", "recurring-revenue"],

    sourceType: "illustrative",
    sourceNote:
      "Modelled from publicly reported micro-SaaS revenue disclosures in the trade-services category. Composite figures, not a specific product.",
    verifiedAt: '2026-07-02',

    featured: true,
    publishedAt: '2026-07-08',
    readingMinutes: 9,
    gradient: "from-[#3B7DD8] via-[#0047AB] to-[#1C1C1C]",
  },

  {
    id: "cs_vending_route",
    slug: "vending-route-suburban",
    title: "An eleven-machine vending route, bought piecemeal",
    dek: "Assembled from four separate sellers over eight months, servicing on Sunday mornings.",
    category: "local-cashflow",
    market: "US Sunbelt suburb",

    startupCostCents: 4_100_000,
    cashInvestedCents: 4_100_000,
    monthlyRevenueCents: 712_000,
    monthlyExpenseCents: 448_000,
    hoursPerWeek: 7,
    monthsToBreakEven: 16,

    effort: "part-time",
    difficulty: "starter",

    body: `Vending is the most oversold and undersold business at once. Oversold as passive income; undersold as an honest, boring, cash-generating route that a person with a van and a free Sunday can genuinely run.

The economics live entirely in placement. The same machine grosses $90 a month in a mediocre location and $420 in a good one, and the machine costs the same either way. Buying a route means buying locations — which is why assembling one from four small sellers beat buying a single listed route: each seller was exiting for their own reason and none of them were priced as a portfolio.

The number nobody quotes is shrinkage plus spoilage, which ran 6% of gross here.`,

    playbookSteps: [
      {
        title: "Underwrite locations, not machines",
        detail:
          "Ask for twelve months of per-machine sales data. Any seller who cannot produce it by machine is selling you an average that hides two dead placements.",
        durationLabel: "Weeks 1–4",
      },
      {
        title: "Confirm the location agreements transfer",
        detail:
          "Most vending placements are handshake deals with a facility manager. Meet every single one before closing. A route is worth nothing if three sites decline the new operator.",
        durationLabel: "Weeks 4–6",
      },
      {
        title: "Buy small and repeatedly",
        detail:
          "Four sellers with two to four machines each priced far below the one broker-listed route of similar size. Small sellers price on what they paid; brokers price on multiples.",
        durationLabel: "Months 1–8",
      },
      {
        title: "Instrument before optimising",
        detail:
          "Telemetry on every machine. Knowing what sold before you drive there converts a full route run into a targeted one, which is where the hours come down.",
        durationLabel: "Month 3",
      },
      {
        title: "Cull the bottom two placements each year",
        detail:
          "Move the machine rather than nursing the location. The relocation cost is recovered in under a quarter at any decent site.",
        durationLabel: "Annual",
      },
    ],

    techStack: [
      "Nayax / Cantaloupe telemetry",
      "Card readers on every machine",
      "Route planning app",
      "Google Sheets for per-machine P&L",
      "Sam's Club business account",
    ],

    risks: [
      "Location loss is the primary risk. Losing one strong placement can take 15% of route revenue with no notice.",
      "Product spoilage and theft ran 6% of gross — routinely omitted from seller pro formas.",
      "It is physical work. Loading a van and servicing eleven machines is real labour, and it does not scale without hiring.",
      "Machine repair skills are effectively mandatory; every service call outsourced erodes a thin margin.",
    ],

    tags: ["route-business", "cash-business", "weekend-operable", "starter"],

    sourceType: "composite",
    sourceNote:
      "Assembled from publicly available vending route listing data and industry shrinkage benchmarks.",
    verifiedAt: '2026-05-30',

    featured: true,
    publishedAt: '2026-06-02',
    readingMinutes: 8,
    gradient: "from-[#0047AB] via-[#002C68] to-[#1C1C1C]",
  },

  {
    id: "cs_notion_templates",
    slug: "notion-template-catalog",
    title: "A catalogue of 14 paid templates",
    dek: "$0 to start, $2,100/mo at month nineteen — and a candid look at why most of the catalogue earns nothing.",
    category: "digital-asset",
    market: "Global, digital",

    startupCostCents: 0,
    cashInvestedCents: 0,
    monthlyRevenueCents: 210_000,
    monthlyExpenseCents: 31_000,
    hoursPerWeek: 5,
    monthsToBreakEven: 1,

    effort: "part-time",
    difficulty: "starter",

    body: `The honest headline: three of fourteen templates produce 81% of the revenue. The other eleven collectively earn less than a part-time shift.

This is the normal shape of a digital-asset catalogue, and it is worth internalising before starting, because the standard advice — "build a library" — misreads the mechanism. The library is not the asset. The distribution is. The three that sell do so because each solved a problem that people were already searching for by name.

Zero startup cost is genuine here, and so is the corollary: zero barrier to entry means every successful template is cloned within a quarter. The defensibility is the audience, not the file.`,

    playbookSteps: [
      {
        title: "Build for a search term, not an idea",
        detail:
          "Every template that worked started as a phrase people already type. The ones that failed started with 'wouldn't it be useful if'.",
        durationLabel: "Week 1",
      },
      {
        title: "Ship the first one in a weekend",
        detail:
          "The first template is tuition, not inventory. Its purpose is to teach you the packaging, delivery, and support load.",
        durationLabel: "Weekend 1",
      },
      {
        title: "Price at $39, not $9",
        detail:
          "Support burden per customer is nearly identical at both prices. The $9 tier attracts the buyers who need the most help.",
        durationLabel: "Ongoing",
      },
      {
        title: "Kill the losers loudly",
        detail:
          "Anything under $50/month after three months comes down. A bloated catalogue dilutes the storefront and adds support surface for no return.",
        durationLabel: "Quarterly",
      },
      {
        title: "Convert buyers to a list",
        detail:
          "The email list is the only durable asset. Every template ships with a reason to subscribe, and the list is what makes launch number four outperform launch number one.",
        durationLabel: "Ongoing",
      },
    ],

    techStack: [
      "Notion",
      "Gumroad",
      "ConvertKit",
      "Canva for listing art",
      "Loom for walkthroughs",
    ],

    risks: [
      "Extreme revenue concentration: three products carry the business, and a marketplace ranking change could halve it overnight.",
      "Zero barrier to entry means direct clones appear within weeks of any success.",
      "Platform risk is total — Gumroad or Notion policy changes are not negotiable from this position.",
      "Revenue decays without new launches. This is not passive; it is episodic.",
    ],

    tags: ["digital-products", "zero-capital", "audience-driven", "starter"],

    sourceType: "illustrative",
    sourceNote:
      "Modelled from publicly disclosed template-creator revenue reports. Composite, not a specific catalogue.",
    verifiedAt: '2026-07-19',

    featured: false,
    publishedAt: '2026-07-24',
    readingMinutes: 7,
    gradient: "from-[#6699EE] via-[#0047AB] to-[#1C1C1C]",
  },

  {
    id: "cs_pressure_washing",
    slug: "pressure-washing-weekend",
    title: "Weekend pressure washing, priced properly",
    dek: "$3,200 of equipment, $145 per driveway, and the pricing mistake that cost the first season.",
    category: "service-business",
    market: "US Southeast",

    startupCostCents: 320_000,
    cashInvestedCents: 320_000,
    monthlyRevenueCents: 174_000,
    monthlyExpenseCents: 79_600,
    hoursPerWeek: 9,
    monthsToBreakEven: 4,

    effort: "part-time",
    difficulty: "starter",

    body: `The first season was priced at $89 a driveway because that is what the cheapest competitor charged. It produced a full calendar and almost no money.

Repricing to $145 lost roughly a third of enquiries and raised profit substantially, because the third that left were the price-shoppers who also took the most scheduling back-and-forth. This is the most repeatable lesson in local services and the hardest to act on: the bottom of the market costs more to serve.

The other structural point is that this business has a hard ceiling at one operator. Twelve jobs a month at nine hours a week is close to the limit of weekend capacity. Growing past it is a different business — hiring, insurance, payroll — and worth entering deliberately rather than drifting into.`,

    playbookSteps: [
      {
        title: "Buy commercial, buy used",
        detail:
          "A consumer pressure washer will not survive a season of driveways. Used commercial equipment at 4 GPM costs about the same as new consumer gear and lasts years longer.",
        durationLabel: "Week 1",
      },
      {
        title: "Insure before the first job",
        detail:
          "General liability runs roughly $500–$800 a year. One etched window or damaged siding without it ends the business.",
        durationLabel: "Week 1",
      },
      {
        title: "Price above the bottom",
        detail:
          "Set the price where you want the customers, then justify it with before-and-after photos on every job. Competing at the floor is a losing structural position.",
        durationLabel: "Ongoing",
      },
      {
        title: "Work one neighbourhood at a time",
        detail:
          "Route density is the whole margin. Six jobs on one street beats twelve scattered across a metro, because drive time is unbilled.",
        durationLabel: "Ongoing",
      },
      {
        title: "Decide the ceiling deliberately",
        detail:
          "At capacity there are two honest options: stay solo and raise prices, or hire and become a manager. Drifting between them is what burns people out.",
        durationLabel: "Month 12",
      },
    ],

    techStack: [
      "4 GPM commercial pressure washer",
      "Surface cleaner attachment",
      "Jobber or Housecall Pro",
      "Square for payments",
      "Google Business Profile + local service ads",
    ],

    risks: [
      "Property damage liability is genuine — etched glass, stripped paint, and water intrusion are all common and expensive.",
      "Highly seasonal in most markets; winter revenue can fall close to zero.",
      "Physically demanding, and it does not scale without hiring.",
      "Effectively zero barrier to entry keeps constant downward price pressure from new entrants.",
    ],

    tags: ["local-services", "weekend-operable", "low-capital", "pricing"],

    sourceType: "composite",
    sourceNote:
      "Economics modelled from equipment pricing and regional service-rate ranges. Not a specific named business.",
    verifiedAt: '2026-06-28',

    featured: false,
    publishedAt: '2026-07-01',
    readingMinutes: 6,
    gradient: "from-[#003A8A] via-[#0047AB] to-[#1C1C1C]",
  },

  {
    id: "cs_solo_scorp",
    slug: "solo-consultant-entity-structure",
    title: "When the S-corp election finally paid",
    dek: "A solo consultant at $138k profit, and the year the maths actually crossed over.",
    category: "solo-structure",
    market: "US",

    startupCostCents: 145_000,
    cashInvestedCents: 145_000,
    monthlyRevenueCents: 1_400_000,
    monthlyExpenseCents: 250_000,
    hoursPerWeek: 32,
    monthsToBreakEven: 1,

    effort: "full-time",
    difficulty: "advanced",

    body: `The S-corp election is the most over-recommended move in solo business, and it does eventually make sense — just far later than the internet suggests.

The mechanism is narrow: profit split into a defensible salary and a distribution, with payroll tax applying only to the salary. Against that saving sit payroll processing, a second tax return, and a materially higher audit profile. Below roughly $50k of profit those costs eat the benefit. Around $80–100k it turns positive. At $138k it is clearly worth doing.

The part that gets people into trouble is "reasonable compensation". There is no safe-harbour percentage, despite what a hundred videos claim. The defensible method is comparable market wages for the work performed, documented at the time — not a ratio chosen because it maximised the saving.

Use the estimator on this site to see the crossover for your own numbers, then take it to an accountant. This is a worked example, not a filing position.`,

    playbookSteps: [
      {
        title: "Run the crossover before electing",
        detail:
          "Model sole proprietor against S-corp at your actual profit, including the full cost of payroll and a second return. The election is a cost until it is a saving.",
        durationLabel: "Before anything",
      },
      {
        title: "Document reasonable compensation properly",
        detail:
          "Pull comparable salary data for the work you actually perform, in your region, and save it. The documentation matters as much as the number.",
        durationLabel: "At election",
      },
      {
        title: "Set up real payroll",
        detail:
          "A payroll service that files the quarterly returns. Paying yourself by transfer and calling it salary is the most common way this falls apart under examination.",
        durationLabel: "Month 1",
      },
      {
        title: "Keep the accounts genuinely separate",
        detail:
          "Separate bank account, separate card, no personal spending from the entity. Commingling undermines the liability protection that motivated the entity in the first place.",
        durationLabel: "Ongoing",
      },
      {
        title: "Revisit annually",
        detail:
          "Profit falls, rules change, states differ. An election that made sense at $138k may not at $70k, and some states tax S-corps in ways that erase the federal saving.",
        durationLabel: "Annual",
      },
    ],

    techStack: [
      "Gusto for payroll",
      "QuickBooks Online",
      "Registered agent service",
      "A CPA who handles the 1120-S",
      "Separate business banking",
    ],

    risks: [
      "Unreasonably low salary is among the most examined positions in small-business tax. There is no safe percentage.",
      "State treatment varies widely; several states impose franchise or entity-level taxes that can eliminate the federal benefit.",
      "Ongoing compliance is permanent — payroll filings and a separate return every year, whether or not the business had a good one.",
      "The saving falls with profit. A bad year can leave you paying for a structure that no longer earns its cost.",
    ],

    tags: ["tax-structure", "s-corp", "solo-operator", "compliance"],

    sourceType: "illustrative",
    sourceNote:
      "A worked educational example using published federal tax parameters. Not tax advice and not a specific taxpayer's return.",
    verifiedAt: '2026-07-30',

    featured: false,
    publishedAt: '2026-08-03',
    readingMinutes: 12,
    gradient: "from-[#E09900] via-[#B87800] to-[#1C1C1C]",
  },
  /* ══════════════════════════════════════════════════════════════════════
     Written for this audience.

     The six studies above are Realiform's, ported unchanged. The three below
     were written for KoreanHub: same discipline, same provenance labelling,
     but the mechanism being taught is one that only bites people who are
     operating across the US–Korea line.
     ══════════════════════════════════════════════════════════════════════ */

  {
    id: "cs_dry_cleaner_succession",
    slug: "korean-dry-cleaner-succession",
    title: "Buying the dry cleaner your parents' generation built",
    dek: "$75k of cash into a $310k shop, bought from a 68-year-old owner with no successor — and the environmental clause that nearly killed it.",
    category: "diaspora-succession",
    market: "US metro with an established Korean commercial district",

    startupCostCents: 31_000_000,
    cashInvestedCents: 7_500_000,
    monthlyRevenueCents: 2_450_000,
    // Operating costs ($19,400) plus debt service on the $235,000 seller note
    // at 6.5% over ten years ($2,668). Quoting the shop's "monthly net" before
    // the note is the same inflation the laundromat teardown calls out.
    monthlyExpenseCents: 2_206_800,
    hoursPerWeek: 45,
    monthsToBreakEven: 31,

    effort: "full-time",
    difficulty: "advanced",

    body: `There is a demographic fact sitting underneath this whole category: the Korean immigrants who built the American dry-cleaning trade in the 1970s and 1980s are now in their late sixties and seventies, and most of their children did not want the business. That produces a specific, time-limited market condition — a large number of profitable, unlisted small businesses whose owners care more about who takes over than about extracting the last twenty thousand dollars of price.

That condition is worth something, and it is worth more to you than to a private-equity search fund, for a reason that has nothing to do with capital. These deals are frequently not brokered. They move through church, through the 상인회 (merchants' association), through a cousin. Being able to have the first conversation in Korean, with the right register, is not a soft advantage — it is the thing that gets you the call before the business is listed, which is where the entire margin lives.

Now the part that offsets it. Dry cleaning is the one small business in this section with a genuine, unbounded environmental liability. Perchloroethylene — perc — was the standard solvent for decades, it is a regulated hazardous substance, and contamination migrates into soil and groundwater under the building. Liability can attach to the current operator and, depending on the state and the structure of the deal, to a purchaser. Remediation is not a five-figure problem. This deal survived because a Phase I environmental site assessment was made a condition of closing and came back clean on a shop that had converted to hydrocarbon solvent in 2009. A shop that ran perc until last year is a different transaction entirely, and possibly not a transaction at all.

The last thing: at forty-five hours a week this is a job that owns an asset, not an asset that pays you. The cash-on-cash figure is real, and it is also compensation for full-time work. Read them separately.`,

    playbookSteps: [
      {
        title: "Work the succession channel, not the listings",
        detail:
          "Merchants' associations, Korean-language commercial papers, church networks, and the supply distributors who sell to every shop in the metro. The distributor's route driver knows which owners are tired before any broker does.",
        durationLabel: "Months 1–4",
      },
      {
        title: "Order the Phase I before you negotiate, not after",
        detail:
          "A Phase I environmental site assessment runs roughly $2,000–$4,000 and takes two to three weeks. Make it a closing condition in writing. If it flags a recognised environmental condition, order a Phase II and be genuinely prepared to walk — this is the one diligence item where the downside has no ceiling.",
        durationLabel: "Weeks 2–6",
      },
      {
        title: "Reconstruct revenue from the tag system",
        detail:
          "Counter tickets and the tagging system produce a per-garment count that cash deposits alone will not. Cross-check twelve months of tickets against deposits and against solvent and hanger purchase volumes. Three independent series that agree is a real number.",
        durationLabel: "Weeks 4–8",
      },
      {
        title: "Read the lease before you value the business",
        detail:
          "In an established Korean commercial district the landlord is often the binding constraint, not the seller. Confirm the remaining term, the assignment clause, and any personal guarantee. A five-year lease against a ten-year note means renegotiating from zero leverage in year five — the same trap as the laundromat.",
        durationLabel: "Weeks 6–9",
      },
      {
        title: "Buy the transition, not just the equipment",
        detail:
          "Ninety days of the seller working beside you, written into the purchase agreement with consideration attached. In a business where the regulars have been coming for twenty years and know the owner by name, an introduction is a transferable asset and an abrupt handover is measurable churn.",
        durationLabel: "Months 1–3 post-close",
      },
    ],

    techStack: [
      "Point-of-sale with garment tagging (SPOT / Enlite)",
      "Hydrocarbon or wet-cleaning system (not perc)",
      "Route delivery software, if the shop runs pickup",
      "QuickBooks Online",
      "Bilingual SMS notification for ready orders",
    ],

    risks: [
      "Environmental liability is the defining risk of this category and it is not capped. Perc contamination can attach to owners and, in some states, to purchasers, and remediation regularly reaches six figures. No Phase I, no deal.",
      "Structural demand decline. Workplace dress codes have loosened for two decades and the trend has not reversed. This is a cash-generating business in a shrinking category — price it accordingly and do not model growth.",
      "Landlord concentration. In dense commercial districts one landlord may hold the whole block, and lease renewal is where the profit of a good year goes.",
      "Equipment age. A boiler or press at end of life is a deferred bill with interest, exactly as with laundromat washers.",
      "The relationship premium cuts both ways. Buying from someone your family knows makes walking away after a bad Phase I socially expensive. Decide in advance that you will.",
    ],

    tags: ["seller-financing", "succession", "acquisition", "korean-american", "environmental-diligence"],

    sourceType: "illustrative",
    sourceNote:
      "Economics modelled from published small-business listing ranges for dry-cleaning plants and from environmental assessment cost ranges. Not a specific named business, and not a specific transaction.",
    verifiedAt: "2026-08-18",

    sources: [
      {
        label: "Dry cleaning sector — regulation of perchloroethylene",
        url: "https://www.epa.gov/stationary-sources-air-pollution/dry-cleaning-facilities-national-perchloroethylene-air-emission",
        publisher: "US Environmental Protection Agency",
      },
      {
        label: "Small business environmental due diligence (Phase I ESA)",
        url: "https://www.epa.gov/brownfields/all-appropriate-inquiries",
        publisher: "US Environmental Protection Agency",
      },
      {
        label: "SBA 7(a) loan program — business acquisition financing",
        url: "https://www.sba.gov/funding-programs/loans/7a-loans",
        publisher: "US Small Business Administration",
      },
    ],

    featured: true,
    publishedAt: "2026-08-18",
    readingMinutes: 12,
    gradient: "from-[#0047AB] via-[#7A091C] to-[#1C1C1C]",
    diasporaAngle:
      "The generation that built the American dry-cleaning trade is retiring without successors. Language and community access get you into the conversation before the business is listed — which is where the whole margin is.",
  },

  {
    id: "cs_f4_remote_saas",
    slug: "running-a-us-saas-from-korea-on-an-f4",
    title: "Running a US micro-SaaS from Seoul on an F-4",
    dek: "$9.8k MRR earned in dollars, lived in won — and the two cross-border tax rules that decide whether you keep it.",
    category: "micro-saas",
    market: "Seoul; customers in the US",

    startupCostCents: 620_000,
    cashInvestedCents: 620_000,
    monthlyRevenueCents: 980_000,
    monthlyExpenseCents: 235_000,
    hoursPerWeek: 25,
    monthsToBreakEven: 9,

    effort: "part-time",
    difficulty: "advanced",

    body: `The business itself is unremarkable, and that is deliberate — the mechanics of a single-wedge B2B tool are covered in the electrician invoicing teardown and nothing about them changes because the founder moved. What changes is everything downstream of the revenue.

An F-4 holder can do this. The visa permits most professional and business activity without employer sponsorship, which is precisely what makes it the useful visa for someone whose income does not come from a Korean employer. Confirm your own position against the current restricted-occupation list before assuming, but for software this is the ordinary case rather than the exception.

The first rule that matters is Korean tax residency. Spend 183 days or more in Korea in a tax year and you are a Korean tax resident, and Korea taxes residents on worldwide income. There is a provision that changes the picture substantially for people in exactly this position: a **foreign national** who has had a domicile or residence in Korea for five years or less within the preceding ten years is taxed on Korea-source income plus only the foreign income actually paid in Korea or remitted to Korea. An F-4 holder is a foreign national. In year two of living in Seoul on US-source SaaS revenue, that distinction is worth more than every optimisation in this section combined — and it expires, on a clock that starts before you think about it.

The second is US self-employment tax. A US citizen owes it on net self-employment earnings regardless of where they live, and the Foreign Earned Income Exclusion does not touch it — the FEIE reduces income tax, not SE tax, which is the single most common and most expensive misunderstanding in this whole area. The instrument that does address it is the US–Korea Totalization Agreement: a self-employed person covered by the Korean National Pension can obtain a certificate of coverage and be exempt from US Social Security and Medicare self-employment tax. That is roughly 15.3% of net earnings, and it is not automatic — you have to be enrolled and you have to get the certificate.

Add the two together and the picture inverts the usual advice. The optimisation is not clever entity structuring. It is: know which side of the five-year line you are on, be genuinely enrolled in the right pension system, and keep the remittance record clean enough to prove what you brought in and when.

None of this is a filing position. Two tax authorities have an opinion about you now, and the cost of a Korean 세무사 and a US CPA who has seen an expatriate return before is trivial against the amounts at stake.`,

    playbookSteps: [
      {
        title: "Establish which residency you actually have",
        detail:
          "Count days honestly for both jurisdictions and write the count down as you go. 183 days in Korea makes you a Korean tax resident; US citizenship makes you a US filer regardless. Being resident in both at once is the normal case here, not an error state.",
        durationLabel: "Before the move",
      },
      {
        title: "Date the five-year clock",
        detail:
          "The relief for foreign nationals resident five years or less within the preceding ten turns on a date. Establish yours, in writing, with a tax professional, at the start — not in the year it runs out. Every decision about when to remit is downstream of this.",
        durationLabel: "Year 1",
      },
      {
        title: "Get the certificate of coverage",
        detail:
          "Enrol in the Korean National Pension, then apply for a certificate of coverage under the US–Korea Totalization Agreement. Without it you pay US self-employment tax on top of Korean contributions. The FEIE will not save you here — it never applied to SE tax.",
        durationLabel: "Year 1",
      },
      {
        title: "Keep the money movement documented",
        detail:
          "Korean banks report inbound foreign-currency receipts above USD 10,000 to the National Tax Service. That is reporting, not permission — but if the five-year provision means you are taxed on what you remit, then what you remitted and when is the taxable fact, and it needs a paper trail you did not reconstruct afterwards.",
        durationLabel: "Ongoing",
      },
      {
        title: "Price the currency exposure instead of ignoring it",
        detail:
          "Revenue is USD, life is KRW, and the pair has moved more than 15% inside a year more than once. Hold a working buffer in the currency you spend rather than converting monthly at whatever the rate happens to be, and never treat a good exchange-rate month as business growth.",
        durationLabel: "Ongoing",
      },
    ],

    techStack: [
      "Stripe (US entity) for billing",
      "Wise or a Korean-corridor remittance service for transfers",
      "US business bank account retained after the move",
      "Korean National Pension enrolment",
      "A Korean 세무사 and a US CPA who handles expatriate returns",
    ],

    risks: [
      "The five-year provision expires. After it does, Korea taxes worldwide income, and a business built around the earlier treatment can face a materially different bill with no change in operations.",
      "Believing the FEIE covers self-employment tax. It does not, it never has, and the gap is roughly 15.3% of net earnings.",
      "Permanent establishment questions. Directing a US company's operations from a desk in Seoul can raise the question of where the business is actually managed. It is a real issue and it is one for a professional, not a calculator.",
      "Currency risk is unhedged and structural — dollar revenue against won costs, with no natural offset.",
      "Visa assumptions. The F-4's employment restrictions have narrowed over time but they exist. Verify your specific activity rather than reasoning from what someone in a group chat does.",
      "Single-founder concentration, exactly as in the domestic version of this business, plus a thirteen-to-sixteen-hour time difference from the customer base.",
    ],

    tags: ["micro-saas", "f4-visa", "cross-border-tax", "remote", "korea-resident"],

    sourceType: "illustrative",
    sourceNote:
      "Economics modelled from publicly reported micro-SaaS revenue disclosures; the tax and visa mechanics are summarised from the primary sources listed below. Composite figures, not a specific product or taxpayer.",
    verifiedAt: "2026-08-18",

    sources: [
      {
        label: "US–Korea Totalization Agreement — self-employment coverage and certificates",
        url: "https://www.ssa.gov/international/Agreement_Pamphlets/korea.html",
        publisher: "US Social Security Administration",
      },
      {
        label: "Foreign Earned Income Exclusion — scope and eligibility tests",
        url: "https://www.irs.gov/individuals/international-taxpayers/foreign-earned-income-exclusion",
        publisher: "Internal Revenue Service",
      },
      {
        label: "Self-employment tax for US citizens living abroad",
        url: "https://www.irs.gov/individuals/international-taxpayers/self-employment-tax-for-businesses-abroad",
        publisher: "Internal Revenue Service",
      },
      {
        label: "Individual income tax — residency and scope of taxation",
        url: "https://www.nts.go.kr/english/",
        publisher: "National Tax Service (국세청)",
      },
      {
        label: "Visa navigator — F-4 activity scope and restrictions",
        url: "https://www.visa.go.kr/openPage.do?MENU_ID=10101",
        publisher: "Korea Visa Portal, Ministry of Justice",
      },
    ],

    featured: true,
    publishedAt: "2026-08-18",
    readingMinutes: 14,
    gradient: "from-[#3B7DD8] via-[#0047AB] to-[#1C1C1C]",
    diasporaAngle:
      "The business is ordinary; the tax position is not. Korean residency rules and the US–Korea totalization agreement decide how much of $9.8k MRR you actually keep, and both turn on dates you set in year one.",
  },

  {
    id: "cs_heritage_language_academy",
    slug: "weekend-korean-language-academy",
    title: "A weekend Korean school with 84 enrolled kids",
    dek: "$18.5k to open, $3,600/mo of profit across a nine-month year — and why the summer is the whole problem.",
    category: "service-business",
    market: "US suburb with a large second-generation Korean population",

    startupCostCents: 1_850_000,
    cashInvestedCents: 1_850_000,
    monthlyRevenueCents: 1_420_000,
    monthlyExpenseCents: 1_060_000,
    hoursPerWeek: 18,
    monthsToBreakEven: 14,

    effort: "part-time",
    difficulty: "intermediate",

    body: `The demand here is not speculative and it is not price-sensitive in the way most local services are. Second- and third-generation parents who lost the language and regret it will pay to keep their children from repeating it. That is a durable motivation, and it is why heritage-language schools survive in markets where a comparable tutoring business would not.

It is also why the honest framing of this business is a nine-month year with a twelve-month cost base. Tuition is collected September through May. Rent, insurance, and the curriculum licence are not. The $3,600 monthly profit figure above is a school-year figure, and the annual number is roughly $32,000, not $43,000. Anyone quoting the monthly number twelve times is doing the same thing the vending sellers do with their averages.

The structural decision that determines everything else is whether teachers are employees or contractors. The instinct is contractors — it is simpler and cheaper. The instinct is usually wrong here: teachers work a schedule you set, in your space, with your curriculum, which is close to the centre of what most jurisdictions treat as employment. Misclassification is assessed with back payroll tax and penalties, and it is one of the more commonly enforced small-business exposures. Model it as payroll from the start; if a professional later tells you contractor status is defensible in your state, that is upside.

The last thing worth saying plainly: the founder is a teacher and an administrator at the same time, and the second job is the one that grows. Eighteen hours a week is enrolment, scheduling, parent messages, and substitute-teacher scrambles far more than it is classroom time.`,

    playbookSteps: [
      {
        title: "Verify demand with a deposit, not a survey",
        detail:
          "Ask twenty families for a refundable $50 enrolment deposit before signing a lease. Parents who say they want a Korean school and parents who will drive there on a Saturday morning for nine months are measurably different populations.",
        durationLabel: "Months 1–2",
      },
      {
        title: "Rent inside an existing institution",
        detail:
          "A church, community centre, or a public school's weekend hours. Signing a standalone commercial lease for a nine-month, one-day-a-week operation is the fastest way to make the economics impossible.",
        durationLabel: "Month 2",
      },
      {
        title: "Settle worker classification before the first hire",
        detail:
          "Get a written view from a professional on whether your teachers are employees in your state, and budget as though they are. Retroactive reclassification arrives with back payroll tax and penalties attached.",
        durationLabel: "Before hiring",
      },
      {
        title: "License a curriculum instead of writing one",
        detail:
          "Established heritage-language curricula exist, including materials distributed through Korean government cultural and education bodies. Writing your own consumes the founder's entire first year and produces something worse.",
        durationLabel: "Month 3",
      },
      {
        title: "Solve the summer deliberately",
        detail:
          "Either shut cleanly and cut every variable cost, or run a short summer intensive priced to cover the fixed base. Drifting through June to August paying rent for an empty room is what turns a workable margin into a marginal one.",
        durationLabel: "Year 1, by March",
      },
    ],

    techStack: [
      "Enrolment and tuition billing (Jackrabbit / Sawyer)",
      "Payroll service",
      "Licensed heritage-language curriculum",
      "Bilingual parent messaging (KakaoTalk channel plus SMS)",
      "General liability insurance with abuse and molestation coverage",
    ],

    risks: [
      "Nine months of revenue against twelve months of fixed cost. Any figure quoted monthly overstates the annual reality by roughly a third.",
      "Worker misclassification. Teachers working your schedule in your space with your curriculum are employees in most jurisdictions, and getting this wrong is assessed retroactively.",
      "Working with minors raises insurance and background-check requirements that a generic small-business policy does not cover. Confirm abuse and molestation coverage specifically.",
      "Enrolment is concentrated and seasonal — a weak September cannot be recovered later in the year.",
      "Founder dependence is near-total. The families enrolled because of a specific person, and the business does not currently survive that person stopping.",
      "Teacher supply is the binding constraint on growth, not demand. Qualified bilingual instructors willing to work Saturday mornings are scarce in most markets.",
    ],

    tags: ["local-services", "heritage-language", "seasonal", "korean-american", "weekend-operable"],

    sourceType: "illustrative",
    sourceNote:
      "Economics modelled from published community-school tuition ranges, facility rental rates, and instructor pay benchmarks. Not a specific named school.",
    verifiedAt: "2026-08-18",

    sources: [
      {
        label: "Independent contractor or employee — classification tests",
        url: "https://www.irs.gov/businesses/small-businesses-self-employed/independent-contractor-self-employed-or-employee",
        publisher: "Internal Revenue Service",
      },
      {
        label: "Overseas Koreans Agency — education and language support programmes",
        url: "https://www.oka.go.kr/",
        publisher: "Overseas Koreans Agency (재외동포청)",
      },
      {
        label: "Korean Cultural Centers worldwide — curriculum and teaching resources",
        url: "https://www.kocis.go.kr/eng/openInformation.do",
        publisher: "Korean Culture and Information Service",
      },
    ],

    featured: false,
    publishedAt: "2026-08-18",
    readingMinutes: 10,
    gradient: "from-[#E09900] via-[#0047AB] to-[#1C1C1C]",
    diasporaAngle:
      "Heritage-language demand is durable and not especially price-sensitive, which is why these schools survive where ordinary tutoring does not. The trap is a nine-month revenue year against a twelve-month cost base.",
  },
];

export const CASE_STUDIES_BY_SLUG: Record<string, CaseStudy> = Object.fromEntries(
  CASE_STUDIES.map((c) => [c.slug, c])
);

export function getCaseStudyBySlug(slug: string | undefined): CaseStudy | undefined {
  return slug ? CASE_STUDIES_BY_SLUG[slug] : undefined;
}

export function getFeaturedCaseStudies(): CaseStudy[] {
  return CASE_STUDIES.filter((c) => c.featured);
}

export function caseStudiesByCategory(category: string): CaseStudy[] {
  return CASE_STUDIES.filter((c) => c.category === category);
}

/** Every category that actually has at least one study, in vocabulary order. */
export function activeCategories(): string[] {
  return [...new Set(CASE_STUDIES.map((c) => c.category))];
}

/** Monthly profit in cents. */
export function monthlyProfitCents(study: CaseStudy): number {
  return study.monthlyRevenueCents - study.monthlyExpenseCents;
}

/**
 * Annualised cash-on-cash against the operator's own cash, as a ratio.
 *
 * Returns null where the metric is not informative: no capital at risk, or a
 * return so far above the capital base that the denominator is doing all the
 * work. A digital product with $4.8k of setup and $62k of annual profit is not
 * a "1,300% return" in any sense a reader should act on — it is a business with
 * almost no capital in it, which payback period expresses honestly.
 */
export function cashOnCashRatio(study: CaseStudy): number | null {
  const invested = study.cashInvestedCents ?? study.startupCostCents;
  if (!invested) return null;
  const ratio = (monthlyProfitCents(study) * 12) / invested;
  return ratio > 5 ? null : ratio;
}

/** Months for monthly profit to return the capital put in. */
export function paybackMonths(study: CaseStudy): number | null {
  const invested = study.cashInvestedCents ?? study.startupCostCents;
  const profit = monthlyProfitCents(study);
  if (profit <= 0) return null;
  return invested / profit;
}
